import React, { useEffect, useMemo, useState } from 'react';

import { Slider as MuiSlider, styled } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import FormBody from 'components/FormBody';
import NumberInput from 'components/Inputs/NumberInput';
import Loader from 'components/Loader/index';
import DeclineOfferThanksPopup from 'pages/Dashboard/steps/AcceptOffer/common/DeclineOfferThanksPopup';
import { MAX_LOAN_AMOUNT_LENGTH } from 'utils/constants';
import { maskMonetaryValue, MONETARY_PREFIX, removeCurrencySign, removeNonDigits } from 'utils/stringUtils';
import OffersTableWrapper from 'pages/Dashboard/steps/AcceptOffer/common/OffersTableWrapper/OffersTableWrapper';
import ConfirmOfferPopup from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers/ConfirmOfferPopup/ConfirmOfferPopup';
import { FormValues } from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers/acceptAutomatedOffer.types';
import { useAppSelector } from 'hooks/useAppSelector';
import DashboardFormHeader from 'components/DashboardFormHeader';
import { ProgressBarStatus } from 'enums/ProgressBarStatus';
import { getBorrowerNames } from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers/acceptAutomatedOffer.utils';
import OffersInformationPopup from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers/OffersInformationPopup';
import { LOAN_AMOUNT_SLIDER_STEP } from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers//acceptAutomatedOffer.constants';
import { Text } from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers//acceptAutomatedOffer.enums';
import styles from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers//AcceptAutomatedOffer.module.scss';
import { OfferDto } from 'api/OffersApi';

const Slider = styled(MuiSlider)({
  '& .MuiSlider-markLabel[data-index="0"]': {
    transform: 'translateX(0%)',
  },
  '& .MuiSlider-markLabel[data-index="1"]': {
    transform: 'translateX(-100%)',
  },
});

const getSliderMarks = (minValue: number, maxValue: number) => {
  return [
    {
      value: minValue,
      label: `${maskMonetaryValue(minValue)}`,
    },
    {
      value: maxValue,
      label: `${maskMonetaryValue(maxValue)}`,
    },
  ];
};

interface AcceptAutomatedOfferProps {
  loanAmount: number;
  setLoanAmount: (loanAmount: number) => void;
  loanOffers: OfferDto[];
  minLoanAmount: number;
  maxLoanAmount: number;
  currentLoanOffer: OfferDto | null;
  setCurrentLoanOffer: (offer: OfferDto) => void;
  onCloseDeclineOfferThanksPopup: () => Promise<void>;
  onDeclineOffer: () => Promise<boolean>;
  onAcceptOffer: () => Promise<void>;
  isLoading: boolean;
}

const AcceptAutomatedOffer = ({
  loanOffers,
  loanAmount,
  setLoanAmount,
  minLoanAmount,
  maxLoanAmount,
  currentLoanOffer,
  setCurrentLoanOffer,
  onCloseDeclineOfferThanksPopup,
  onDeclineOffer,
  onAcceptOffer,
  isLoading,
}: AcceptAutomatedOfferProps) => {
  const { variables } = useAppSelector((state) => state.application);
  const [isDeclineOfferThanksPopupOpen, setIsDeclineOfferThanksPopupOpen] = useState(false);
  const [isConfirmOfferPopupOpen, setIsConfirmOfferPopupOpen] = useState(false);
  const [isOtherOffersVisible, setIsOtherOffersVisible] = useState(false);
  const { lowestPaymentOffers, otherOffers } = useMemo(() => {
    const lowestPaymentItems = loanOffers.filter((offer: OfferDto) => {
      return offer.isBestPayment;
    });
    const othersItems = loanOffers.filter((offer: OfferDto) => {
      return !offer.isBestPayment;
    });

    return { lowestPaymentOffers: lowestPaymentItems, otherOffers: othersItems };
  }, [loanOffers]);
  const {
    formState: { errors, isSubmitted, isValid },
    setValue,
    clearErrors,
    trigger,
    watch,
    control,
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      loanAmount: loanAmount,
    },
    shouldFocusError: true,
  });
  const watcher = watch();

  useEffect(() => {
    const updateLoanAmount = async () => {
      const isLoanAmountValid = await trigger('loanAmount');

      if (!isLoanAmountValid) {
        return;
      }

      setLoanAmount(watcher.loanAmount);
    };

    updateLoanAmount();
  }, [setLoanAmount, trigger, watcher.loanAmount]);

  useEffect(() => {
    if (watcher.loanAmount !== loanAmount) {
      clearErrors('loanAmount');
      setValue('loanAmount', loanAmount);
    }
  }, [loanAmount]);

  const handleLoanAmountInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = +removeNonDigits(removeCurrencySign(event.target.value));

    if (watcher.loanAmount !== newValue) {
      clearErrors('loanAmount');
      setValue('loanAmount', newValue);
    }

    if (isSubmitted) {
      trigger();
    }
  };

  const handleLoanAmountSliderChangeCommitted = (
    event: Event | React.SyntheticEvent<Element, Event>,
    value: number | number[],
  ) => {
    clearErrors('loanAmount');
    setValue('loanAmount', value as number);

    if (isSubmitted) {
      trigger();
    }
  };

  const handleDeclineOffer = async () => {
    const isSuccess = await onDeclineOffer();

    if (isSuccess) {
      setIsDeclineOfferThanksPopupOpen(true);
    }
  };

  const handleCloseDeclineOfferThanksPopup = async () => {
    setIsDeclineOfferThanksPopupOpen(false);
    await onCloseDeclineOfferThanksPopup();
  };

  const handleCloseConfirmOfferPopup = async () => {
    setIsConfirmOfferPopupOpen(false);
  };

  const selectLoanOffer = (loanOffer: OfferDto) => {
    trigger();
    setCurrentLoanOffer(loanOffer);
    setIsConfirmOfferPopupOpen(true);
  };

  const showOtherOffers = () => {
    setIsOtherOffersVisible(true);
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div>
          <DashboardFormHeader
            mainMessage={
              <>
                <div>{getBorrowerNames(variables!.borrower_first_name!, variables!.coborrower_first_name!)},</div>
                <div>
                  You qualify for offers up to{' '}
                  <span className={styles.headerMaxLoanAmount}>{maskMonetaryValue(maxLoanAmount)}</span>.
                </div>
              </>
            }
            percent={ProgressBarStatus.Done}
            isDefaultStyles
          />
          <FormBody formBodyClassName={styles.formBody}>
            <Controller
              name="loanAmount"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  if (value > maxLoanAmount) {
                    return `Cash to You must be less or equal to ${maskMonetaryValue(maxLoanAmount)}`;
                  }

                  if (value < minLoanAmount) {
                    return `Cash to You must be greater or equal to ${maskMonetaryValue(minLoanAmount)}`;
                  }
                },
              }}
              render={({ field }) => (
                <div className={styles.loanAmountInputWrapper}>
                  <NumberInput
                    ref={field.ref}
                    label={Text.CashToYou}
                    prefix={MONETARY_PREFIX}
                    placeholder={MONETARY_PREFIX}
                    thousandSeparator
                    inputClassName={styles.loanAmountInput}
                    labelClassName={styles.loanAmountLabel}
                    name="loanAmount"
                    onBlur={handleLoanAmountInputChange}
                    errorMessage={errors.loanAmount?.message}
                    value={watcher.loanAmount}
                    maxLength={MAX_LOAN_AMOUNT_LENGTH}
                    allowNegative={false}
                    onKeyDown={(event: any) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();

                        handleLoanAmountInputChange(event);
                      }
                    }}
                  />
                </div>
              )}
            />
            <div className={styles.loanAmountSliderWrapper}>
              <Slider
                key={`slider-${watcher.loanAmount}`}
                defaultValue={watcher.loanAmount}
                step={LOAN_AMOUNT_SLIDER_STEP}
                min={minLoanAmount}
                max={maxLoanAmount}
                valueLabelDisplay="auto"
                classes={{
                  root: styles.loanAmountSlider,
                  mark: styles.loanAmountSliderMark,
                  markLabel: styles.loanAmountSliderMarkLabel,
                  rail: styles.loanAmountSliderRail,
                  thumb: styles.loanAmountSliderThumb,
                }}
                marks={getSliderMarks(minLoanAmount, maxLoanAmount)}
                onChangeCommitted={handleLoanAmountSliderChangeCommitted}
              />
            </div>
            <div className={styles.tableTitle}>Lowest Payment Offers</div>
            <OffersTableWrapper
              loanOffers={lowestPaymentOffers}
              onAcceptOffer={selectLoanOffer}
              isDisabled={!isValid}
            />
            {!!otherOffers.length && (
              <>
                {!isOtherOffersVisible && (
                  <div className={styles.showOtherOfferButton} onClick={showOtherOffers}>
                    Show Other Offers
                  </div>
                )}
                {isOtherOffersVisible && (
                  <>
                    <div className={styles.tableTitle}>Other Offers</div>
                    <OffersTableWrapper
                      loanOffers={otherOffers}
                      onAcceptOffer={selectLoanOffer}
                      isDisabled={!isValid}
                    />
                  </>
                )}
              </>
            )}
            <span className={styles.declineOfferButton} onClick={handleDeclineOffer}>
              {Text.Decline}
            </span>
          </FormBody>
          {isConfirmOfferPopupOpen && (
            <ConfirmOfferPopup
              cashToYou={watcher.loanAmount}
              loanOffer={currentLoanOffer!}
              isModalOpen={isConfirmOfferPopupOpen}
              onSubmit={onAcceptOffer}
              onClose={handleCloseConfirmOfferPopup}
            />
          )}
          <DeclineOfferThanksPopup
            isModalOpen={isDeclineOfferThanksPopupOpen}
            onClose={handleCloseDeclineOfferThanksPopup}
          />
          <OffersInformationPopup maxLoanAmount={maxLoanAmount} />
        </div>
      )}
    </>
  );
};

export default AcceptAutomatedOffer;
